<template>
  <div
    class="tx-dice-row"
    :class="{ 'tx-dice-row--float': floatResult }"
    :aria-label="`Dice result: ${resultDice.join(', ')}`"
  >
    <!--
      Closed (betting over, no result yet): three dice tumble, cycling through
      all six faces, until the result arrives.
    -->
    <template v-if="isClosed">
      <span v-for="index in 3" :key="`spin-${index}`" class="tx-die">
        <span class="tx-die__spin" :style="{ '--spin-delay': `${-(index - 1) * 140}ms` }">
          <img
            v-for="face in 6"
            :key="face"
            class="tx-die__face"
            :src="diceImage(face)"
            alt=""
            :style="{ '--face-delay': `${-((face - 1) * 90 + (index - 1) * 200)}ms` }"
          />
        </span>
      </span>
    </template>

    <!--
      Result: each die stops in turn, 0 / 0.12 / 0.24s, and drops onto its face.
    -->
    <template v-else-if="isResultPlaying">
      <span v-for="(die, index) in tableResultDice" :key="`land-${animationKey}-${index}`" class="tx-die">
        <span
          class="tx-die__spin tx-die__spin--out"
          :style="{ '--spin-delay': `${-index * 140}ms`, '--stop-delay': `${index * DIE_STOP_STEP_MS}ms` }"
        >
          <img
            v-for="face in 6"
            :key="face"
            class="tx-die__face"
            :src="diceImage(face)"
            alt=""
            :style="{ '--face-delay': `${-((face - 1) * 90 + index * 200)}ms` }"
          />
        </span>
        <img
          class="tx-die__land"
          :src="diceImage(die)"
          :alt="`Die ${index + 1}: ${die}`"
          :style="{ '--stop-delay': `${index * DIE_STOP_STEP_MS}ms` }"
        />
      </span>
    </template>

    <template v-else>
      <span v-for="(die, index) in resultDice" :key="`still-${index}`" class="tx-die">
        <img :src="diceImage(die)" :alt="`Die ${index + 1}: ${die}`" />
      </span>
    </template>

    <!--
      "= 12", beside the dice: the "=" slides in, then the total pops (0.4 -> 1.3
      -> 0.94 -> 1) and its gold glow pulses twice.
    -->
    <span
      v-if="resultReady"
      :key="`result-${animationKey}`"
      class="tx-dice-row__result"
      :class="[resultSide, { 'tx-dice-row__result--animated': isResultPlaying }]"
    >
      <span class="tx-dice-row__equals">=</span>
      <strong class="tx-dice-row__total">{{ resultTotal }}</strong>
      <!-- <small v-if="resultLabel && !floatResult" class="tx-dice-row__label">{{ resultLabel }}</small> -->
    </span>
  </div>
</template>

<script>
import { getLatestTableDice, getTaiXiuDice } from '@/store/modules/tai-xiu';
import { TaiXiuRoundStatus } from '@/utils/tai-xiu.model';

const ROLLING_STATUSES = [
  TaiXiuRoundStatus.betClosed,
  TaiXiuRoundStatus.inPlay,
  TaiXiuRoundStatus.ended,
];
const RESULT_STATUSES = [
  TaiXiuRoundStatus.declared,
  TaiXiuRoundStatus.revealing,
  TaiXiuRoundStatus.settled,
];
// Dice stop one by one this far apart (GA28 prototype: 0 / 0.12 / 0.24s).
const DIE_STOP_STEP_MS = 120;

/*
 * The three result dice, with the roll and result animation.
 *
 * Follows the GA28 prototype (V2.1.1):
 *   closed  -- the dice tumble (rotate 420ms, a new face every 90ms), looping
 *   result  -- the dice stop one by one 0.12s apart and drop onto their face;
 *              the "=" slides in at 0.36s, the total pops in at 0.38s and its
 *              gold glow pulses twice; then it holds until the next round
 *   open    -- the last result, still
 * It is all CSS keyframes (scoped, so they are namespaced per component).
 */
export default {
  name: 'TaiXiuDiceRow',
  props: {
    table: {
      type: Object,
      required: true,
    },
    isRolling: {
      type: Boolean,
      default: false,
    },
    /*
     * The total sits just outside the dice instead of in the row, so the dice
     * keep their place when it appears. For a bar that centres the dice (the
     * desktop pane's); elsewhere the total stays inline as before.
     */
    floatResult: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      DIE_STOP_STEP_MS,
      // The roll has been seen in this session, so this round's result is
      // animated in; a round that is already decided on load is just shown.
      wasRolling: false,
      animateResult: false,
    };
  },
  computed: {
    tableResultDice() {
      // This round's own result, or null while it has none.
      return getTaiXiuDice(this.table.result) || null;
    },
    resultDice() {
      // The latest result known for the table. There is deliberately no
      // fallback that derives dice from a total: the old one produced
      // `lastResult || 12` -> [4, 4, 4] on every round with no parsed result.
      return getLatestTableDice(this.table) || [];
    },
    isRollingStatus() {
      // Betting closed until the result is declared.
      return ROLLING_STATUSES.includes(this.table.fightStatus);
    },
    // This round's result is in.
    resultReady() {
      return !!this.tableResultDice && RESULT_STATUSES.includes(this.table.fightStatus);
    },
    isClosed() {
      return (this.isRolling || this.isRollingStatus) && !this.resultReady;
    },
    isResultPlaying() {
      return this.resultReady && this.animateResult;
    },
    resultTotal() {
      return this.tableResultDice ? this.tableResultDice.reduce((sum, face) => sum + face, 0) : 0;
    },
    resultSide() {
      return this.resultTotal > 10 ? 'tai' : 'xiu';
    },
    resultLabel() {
      // Same BIG/SMALL split as the road charts (total > 10 is BIG).
      return this.resultTotal > 10 ? 'BIG' : 'SMALL';
    },
    // Restarts the animation for a new round.
    animationKey() {
      return `${this.table.currentFightId || ''}|${this.table.result || ''}`;
    },
  },
  watch: {
    isClosed: {
      immediate: true,
      handler(closed) {
        if (closed) this.wasRolling = true;
      },
    },
    resultReady: {
      immediate: true,
      handler(ready) {
        if (!ready) {
          this.animateResult = false;
          return;
        }
        // Rolled in front of us -> animate the landing; opened on a finished
        // round -> nothing to play.
        this.animateResult = this.wasRolling;
        this.wasRolling = false;
      },
    },
  },
  methods: {
    diceImage(face) {
      return require(`@/assets/svg/dice-items/dice-${face}.svg`);
    },
  },
};
</script>

<style lang="scss" scoped>
.tx-dice-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
}

// One die's slot: the spinner, the landed face and the still face share it.
.tx-die {
  position: relative;
  flex: none;
  width: 15px;
  height: 15px;

  img {
    width: 100%;
    height: 100%;
    display: block;
  }
}

.tx-die__spin {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  animation: tx-die-spin 420ms linear infinite;
  animation-delay: var(--spin-delay, 0ms);

  // Gone the moment its die stops (the landed face is on top from then on).
  &--out {
    animation: tx-die-spin 420ms linear infinite, tx-die-out 1ms linear forwards;
    animation-delay: var(--spin-delay, 0ms), var(--stop-delay, 0ms);
  }
}

.tx-die__face {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  animation: tx-die-face 540ms steps(1) infinite;
  animation-delay: var(--face-delay, 0ms);
}

.tx-die__land {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  animation: tx-die-land 200ms cubic-bezier(0.2, 0.8, 0.3, 1.2) both;
  animation-delay: var(--stop-delay, 0ms);
}

.tx-dice-row__result {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: 4px;
  white-space: nowrap;
  line-height: 1;

  .tx-dice-row__equals {
    font-size: 13px;
    font-weight: 700;
    color: #8ba1ba;
  }

  .tx-dice-row__total {
    display: inline-block;
    font-size: 16px;
    font-weight: 800;
    color: #f3e2a6;
  }

  .tx-dice-row__label {
    font-size: 11px;
    font-weight: 700;
  }

  &.tai .tx-dice-row__label {
    color: #ff5a4a;
  }

  &.xiu .tx-dice-row__label {
    color: #4da3ff;
  }

  &--animated {
    .tx-dice-row__equals {
      animation: tx-die-tag 160ms ease-out 360ms both;
    }

    .tx-dice-row__total {
      animation: tx-die-total 380ms cubic-bezier(0.2, 0.8, 0.3, 1.2) 380ms both,
        tx-die-glow 600ms ease-in-out 780ms 2;
    }
  }
}

// Outside the row's own width, so the dice stay where they were: the bar's
// pill is sized by the dice alone and the total hangs off its right edge.
// (The row is the containing block: the header positions it absolutely.)
.tx-dice-row--float {
  .tx-dice-row__result {
    position: absolute;
    left: 100%;
    top: 50%;
    transform: translateY(-50%);
    margin-left: 5px;
    gap: 5px;

    .tx-dice-row__equals {
      font-size: 14px;
    }

    .tx-dice-row__total {
      font-size: 19px;
    }
  }
}

@keyframes tx-die-spin {
  0% {
    transform: rotate(0) scale(0.92);
  }
  50% {
    transform: rotate(180deg) scale(1.04);
  }
  100% {
    transform: rotate(360deg) scale(0.92);
  }
}

// Six faces stacked, each lit for a sixth of the cycle.
@keyframes tx-die-face {
  0%,
  16.6% {
    opacity: 1;
  }
  16.7%,
  100% {
    opacity: 0;
  }
}

@keyframes tx-die-out {
  to {
    opacity: 0;
    visibility: hidden;
  }
}

@keyframes tx-die-land {
  0% {
    transform: translateY(-7px) scale(0.55);
    opacity: 0;
  }
  70% {
    transform: translateY(1px) scale(1.06);
    opacity: 1;
  }
  100% {
    transform: none;
    opacity: 1;
  }
}

@keyframes tx-die-tag {
  0% {
    transform: translateX(-6px);
    opacity: 0;
  }
  100% {
    transform: none;
    opacity: 1;
  }
}

@keyframes tx-die-total {
  0% {
    transform: scale(0.4);
    opacity: 0;
  }
  45% {
    transform: scale(1.32);
    opacity: 1;
  }
  70% {
    transform: scale(0.94);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes tx-die-glow {
  0%,
  100% {
    text-shadow: 0 0 0 rgba(243, 226, 166, 0);
  }
  50% {
    text-shadow: 0 0 10px rgba(243, 226, 166, 0.95), 0 0 18px rgba(220, 190, 114, 0.7);
  }
}

// No tumbling or pops: the spinner shows one still face.
@media (prefers-reduced-motion: reduce) {
  .tx-die__spin,
  .tx-die__spin--out,
  .tx-die__face,
  .tx-die__land,
  .tx-dice-row__result--animated .tx-dice-row__equals,
  .tx-dice-row__result--animated .tx-dice-row__total {
    animation: none;
  }

  .tx-die__face {
    opacity: 0;

    &:first-child {
      opacity: 1;
    }
  }
}
</style>
